import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-header-profile',
  standalone: true,
  imports: [
    CommonModule, 
    MatToolbarModule, 
    MatIconModule, 
    MatButtonModule,
    MatBadgeModule
  ],
  templateUrl: './header-profile.component.html',
  styleUrl: './header-profile.component.scss'
})
export class HeaderProfileComponent implements OnInit, OnDestroy {
  @Input() userName: string = 'Usuario';
  @Input() notificationCount: number = 0;
  
  isMobileMenuOpen: boolean = false;
  currentSection: string = 'home';
  showNotifications: boolean = true;
  isPasswordChangeSection: boolean = false;
  isUserDataCollapsed: boolean = false; // Estado del colapso de datos de usuario
  
  userDisplayName: string = '';
  userMembership: string = '';
  userBirthdate: string = '';
  userStartDate: string = '';
  userEndDate: string = '';
  
  private destroy$ = new Subject<void>();
  
  @Output() notificationClicked = new EventEmitter<void>();
  @Output() menuClicked = new EventEmitter<void>();
  @Output() navigationClicked = new EventEmitter<string>();

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      this.updateHeaderState(event.url);
    });
    
    this.updateHeaderState(this.router.url);
    this.loadUserData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateHeaderState(url: string): void {
    this.isPasswordChangeSection = false;
    
    if (url.includes('/cambio-contrasena')) {
      this.currentSection = 'cambio-contrasena';
      this.showNotifications = true;
      this.isPasswordChangeSection = true;
    } else if (url.includes('/services') || url.includes('/servicios')) {
      this.currentSection = 'servicios';
      this.showNotifications = true;
    } else if (url.includes('/beneficios')) {
      this.currentSection = 'beneficios';
      this.showNotifications = true;
    } else if (url.includes('/solicitudes')) {
      this.currentSection = 'solicitudes';
      this.showNotifications = true;
    } else if (url.includes('/perfil') || url.includes('/profile')) {
      this.currentSection = 'perfil';
      this.showNotifications = true;
    } else if (url.includes('/gestion-perfil')) {
      this.currentSection = 'gestion-perfil';
      this.showNotifications = true;
    } else if (url.includes('/home')) {
      this.currentSection = 'home';
      this.showNotifications = true;
    } else {
      this.currentSection = 'home';
      this.showNotifications = true;
    }
  }

  /**
   * Verifica si un botón de navegación está activo
   */
  isNavItemActive(section: string): boolean {
    return this.currentSection === section;
  }

  /**
   * Navega a una ruta específica
   * @param route Ruta a la que navegar
   */
  navigateTo(route: string): void {
    this.navigationClicked.emit(route);
    this.router.navigate([route]);
  }

  /**
   * Maneja el clic en el botón de notificaciones
   */
  onNotificationClick(): void {
    this.notificationClicked.emit();
    console.log('Notificaciones clickeadas');
  }

  /**
   * Maneja el clic en el menú hamburguesa (móvil)
   */
  onMenuClick(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.menuClicked.emit();
    console.log('Menú clickeado - Estado:', this.isMobileMenuOpen ? 'Abierto' : 'Cerrado');
  }

  /**
   * Cierra el menú móvil
   */
  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  /**
   * Navega desde el menú móvil y lo cierra
   */
  navigateFromMobile(route: string): void {
    this.navigateTo(route);
    this.closeMobileMenu();
  }

  /**
   * Navega a administrar perfil
   */
  navigateToManageProfile(): void {
    this.navigateTo('/gestion-perfil');
  }

  /**
   * Verifica si se debe mostrar el botón administrar perfil
   */
  shouldShowManageProfile(): boolean {
    return this.currentSection !== 'gestion-perfil';
  }

  /**
   * Abre las notificaciones desde el menú móvil
   */
  onNotificationFromMobile(): void {
    this.closeMobileMenu();
    // Pequeño delay para que se cierre el menú primero
    setTimeout(() => {
      this.onNotificationClick();
    }, 300);
  }

  private loadUserData(): void {
    const res = localStorage.getItem('user');
    const user = res ? JSON.parse(res) : null;
    
    if (user) {
      this.userDisplayName = user.name || this.userName || 'Usuario';
      this.userMembership = user.memberships || '000000';
      this.userBirthdate = user.birthdate || '';
      this.userStartDate = user.startdate || '';
      this.userEndDate = user.enddate || '';
    }
  }

  getUserDisplayName(): string {
    return this.userDisplayName;
  }

  getUserMembership(): string {
    return this.userMembership;
  }

  getUserBirthdate(): string {
    return this.userBirthdate;
  }

  getUserStartDate(): string {
    return this.userStartDate;
  }

  getUserEndDate(): string {
    return this.userEndDate;
  }

  /**
   * Formatea una fecha al formato DD-MM-YYYY
   */
  formatDate(dateString: string): string {
    if (!dateString) return '--';
    
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateString; // Si no se puede formatear, devolver el string original
    }
  }

  /**
   * Alterna el estado de colapso de los datos del usuario
   */
  toggleUserData(): void {
    this.isUserDataCollapsed = !this.isUserDataCollapsed;
  }

  /**
   * Verifica si la cuenta es de Banamex
   */
  isAccountBanamex(): boolean {
    let allowedAccounts = ['2228', '2665', '2559', '2907', '2988', '2997', '2998', '2999'];
    const account = localStorage.getItem('account');
    return account ? allowedAccounts.includes(account) : false;
  }
  
  logout() {
    localStorage.clear();
    this.router.navigate(['/login']);
  }
}
